import { api } from './api';

// ──────────────────────────────────────────────────────────────────────────────
// Types — mirror backend AdminService row shapes (snake_case from the DB).
// ──────────────────────────────────────────────────────────────────────────────

export interface AdminStats {
  users_total: number;
  companies_total: number;
  workspaces_total: number;
  contacts_total: number;
  accounts_total: number;
  signals_last_24h: number;
  deals_open: number;
  ai_runs_today: number;
  ai_spend_today_cents: number;
  audit_events_today: number;
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: 'owner' | 'admin' | 'member';
  primary_locale: string | null;
  created_at: string;
  last_login_at: string | null;
  email_verified: boolean;
  company_id: string | null;
  company_name: string | null;
}

export interface AdminCompany {
  id: string;
  name: string;
  country_code: string;
  plan: string;
  verification_status: string;
  ai_monthly_cap_cents: number | null;
  created_at: string;
  seat_count: number;
}

export interface AdminCompanyDetail {
  company: AdminCompany;
  workspaces: Array<{ id: string; name: string; created_at: string }>;
}

export interface AdminAiRun {
  id: string;
  workspace_id: string;
  user_id: string;
  agent_kind: string;
  model: string;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  cost_cents: number | null;
  cited_record_kinds: string[] | null;
  cited_record_ids: string[] | null;
  created_at: string;
}

export interface AdminAiSpend {
  workspace_id: string;
  workspace_name: string;
  company_id: string;
  company_name: string;
  run_count: number;
  total_cost_cents: number;
}

export interface AdminAuditRow {
  id: string;
  company_id: string;
  workspace_id: string | null;
  user_id: string | null;
  action: string;
  target_kind: string | null;
  target_id: string | null;
  payload: Record<string, unknown>;
  occurred_at: string;
}

export interface Paged<T> {
  items: T[];
  total: number;
}

// ──────────────────────────────────────────────────────────────────────────────

export const adminApi = {
  stats: async (): Promise<AdminStats> =>
    (await api.get<AdminStats>('/api/admin/stats')).data,

  listUsers: async (params: {
    search?: string;
    role?: 'owner' | 'admin' | 'member';
    limit?: number;
    offset?: number;
  } = {}): Promise<Paged<AdminUser>> =>
    (await api.get<Paged<AdminUser>>('/api/admin/users', { params })).data,

  getUser: async (id: string): Promise<AdminUser> =>
    (await api.get<AdminUser>(`/api/admin/users/${id}`)).data,

  updateUserRole: async (id: string, role: 'owner' | 'admin' | 'member'): Promise<AdminUser> =>
    (await api.patch<AdminUser>(`/api/admin/users/${id}/role`, { role })).data,

  listCompanies: async (params: {
    search?: string;
    plan?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<Paged<AdminCompany>> =>
    (await api.get<Paged<AdminCompany>>('/api/admin/companies', { params })).data,

  getCompany: async (id: string): Promise<AdminCompanyDetail> =>
    (await api.get<AdminCompanyDetail>(`/api/admin/companies/${id}`)).data,

  listAiRuns: async (params: {
    workspaceId?: string;
    agentKind?: string;
    model?: string;
    sinceDays?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<Paged<AdminAiRun>> =>
    (await api.get<Paged<AdminAiRun>>('/api/admin/ai-runs', { params })).data,

  aiSpend: async (sinceDays = 30): Promise<AdminAiSpend[]> =>
    (await api.get<AdminAiSpend[]>('/api/admin/ai-spend', { params: { sinceDays } })).data,

  listAudit: async (params: {
    companyId?: string;
    userId?: string;
    action?: string;
    sinceDays?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<Paged<AdminAuditRow>> =>
    (await api.get<Paged<AdminAuditRow>>('/api/admin/audit', { params })).data,
};
